import { Link } from "react-router-dom";
import { ArrowLeft, Package, Truck, Thermometer } from "lucide-react";

export default function StorageLogisticsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Storage & Logistics</h1>
          <p className="text-gray-200 mt-2">
            Complete guide to warehouse, storage, and logistics space solutions
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Overview */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <div className="flex items-start gap-4 mb-6">
            <Package className="w-8 h-8 text-[var(--secondary)] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-3xl font-bold mb-4">
                Storage & Logistics Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Find flexible storage and logistics solutions for your business
                needs. From small inventory storage to large-scale warehousing
                and distribution centers, AirArrange connects you with the right
                space for efficient operations and growth.
              </p>
            </div>
          </div>
        </section>

        {/* For Tenants */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Storage Tenants</h2>

          <div className="space-y-8">
            {/* Types of Storage Spaces */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Types of Storage & Logistics Spaces
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Small-Scale Storage
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Perfect for small businesses, e-commerce sellers, and
                    seasonal inventory.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Climate-controlled storage units (100-500 sq ft)</li>
                    <li>Business storage lockers</li>
                    <li>Archive and document storage</li>
                    <li>Shared warehouse space</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Fulfillment Centers
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Spaces optimized for e-commerce order processing and
                    shipping.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Pick and pack facilities</li>
                    <li>Order processing centers</li>
                    <li>Returns processing areas</li>
                    <li>Multi-channel fulfillment hubs</li>
                  </ul>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Warehouse & Distribution
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Large-scale storage for inventory management and
                    distribution.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Standard warehouses (5,000+ sq ft)</li>
                    <li>Distribution centers</li>
                    <li>Cross-docking facilities</li>
                    <li>Third-party logistics (3PL) spaces</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Specialized Storage
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Storage with specific environmental or security requirements.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Cold storage and refrigerated warehouses</li>
                    <li>Hazardous materials storage (with permits)</li>
                    <li>High-security vaults</li>
                    <li>Wine and art storage</li>
                  </ul>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Flex Warehouse Space
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Combination spaces with storage and workspace areas.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Warehouse with office space</li>
                    <li>Light assembly and storage combo</li>
                    <li>Showroom and storage facilities</li>
                    <li>Workshop and inventory spaces</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Key Features to Consider */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Truck className="w-6 h-6 text-blue-600" />
                Key Features to Consider
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">
                    Access & Loading Facilities
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>Loading Docks:</strong> Number of docks and dock
                      height (typically 48-52 inches)
                    </li>
                    <li>
                      <strong>Drive-In Doors:</strong> Grade-level access for
                      trucks and containers
                    </li>
                    <li>
                      <strong>Dock Levelers:</strong> Equipment to bridge gap
                      between dock and truck
                    </li>
                    <li>
                      <strong>Truck Court Depth:</strong> Space for maneuvering
                      large vehicles
                    </li>
                    <li>
                      <strong>Access Hours:</strong> 24/7 availability for
                      flexible operations
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">
                    Storage Infrastructure
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>Ceiling Height:</strong> Clear height for
                      maximizing vertical storage (18-30+ feet)
                    </li>
                    <li>
                      <strong>Floor Load Capacity:</strong> Weight limits for
                      racking and inventory
                    </li>
                    <li>
                      <strong>Racking Systems:</strong> Existing shelving or
                      pallet racking availability
                    </li>
                    <li>
                      <strong>Column Spacing:</strong> Bay size affecting layout
                      efficiency
                    </li>
                    <li>
                      <strong>Floor Type:</strong> Concrete quality and flatness
                      for equipment
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-blue-500" />
                    Climate Control
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      Temperature-controlled spaces for sensitive products
                    </li>
                    <li>Humidity control for electronics or paper goods</li>
                    <li>Refrigerated storage (33-40°F) for perishables</li>
                    <li>Freezer storage (0°F or below) for frozen goods</li>
                    <li>Insulation quality for energy efficiency</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Security Features</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>24/7 video surveillance and monitoring</li>
                    <li>Gated access with key cards or codes</li>
                    <li>Security personnel on-site</li>
                    <li>Individual unit alarms</li>
                    <li>Fire suppression and detection systems</li>
                    <li>Perimeter fencing and lighting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Calculating Space Needs */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Calculating Your Storage Space Needs
              </h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <p className="text-gray-700 mb-4">
                  Use this formula to estimate required warehouse space:
                </p>
                <div className="bg-white rounded-lg p-4 mb-4">
                  <p className="font-mono text-sm mb-2">
                    Total Space = (Inventory Volume) ÷ (Storage Density) ×
                    (Safety Factor)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>
                      <strong>Inventory Volume:</strong> Total cubic feet of
                      products
                    </li>
                    <li>
                      <strong>Storage Density:</strong> % of space actually used
                      (typically 60-80%)
                    </li>
                    <li>
                      <strong>Safety Factor:</strong> Growth buffer (typically
                      1.2-1.5x)
                    </li>
                  </ul>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Pro Tip:</strong> Don't forget to account for
                    workspace, aisles, packing areas, and office space in your
                    calculations.
                  </p>
                </div>
              </div>
            </div>

            {/* Cost Considerations */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Understanding Storage Costs
              </h3>
              <p className="text-gray-700 mb-3">
                Beyond base rent, consider these additional costs:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Direct Costs</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Base rent (per sq ft/month)</li>
                    <li>• Utilities (electricity, water, gas)</li>
                    <li>• Climate control premiums</li>
                    <li>• Property taxes (usually in NNN leases)</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Operational Costs</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Racking and shelving systems</li>
                    <li>• Forklifts and material handling equipment</li>
                    <li>• Warehouse management software</li>
                    <li>• Labor costs for staff</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Insurance & Safety</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Property insurance</li>
                    <li>• Inventory insurance</li>
                    <li>• Liability coverage</li>
                    <li>• Workers' compensation</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Maintenance & Services
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• CAM (Common Area Maintenance) fees</li>
                    <li>• Waste removal services</li>
                    <li>• Security monitoring fees</li>
                    <li>• Pest control services</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Setup Checklist */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Warehouse Setup Checklist
              </h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="floor-plan"
                    />
                    <label htmlFor="floor-plan" className="text-gray-700">
                      Design efficient floor plan and product flow
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="racking"
                    />
                    <label htmlFor="racking" className="text-gray-700">
                      Install racking systems and storage infrastructure
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="equipment"
                    />
                    <label htmlFor="equipment" className="text-gray-700">
                      Acquire forklifts, pallet jacks, and handling equipment
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="wms"
                    />
                    <label htmlFor="wms" className="text-gray-700">
                      Implement warehouse management system (WMS)
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="safety-systems"
                    />
                    <label htmlFor="safety-systems" className="text-gray-700">
                      Set up safety systems and emergency procedures
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="security"
                    />
                    <label htmlFor="security" className="text-gray-700">
                      Install security cameras and access control
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="labeling"
                    />
                    <label htmlFor="labeling" className="text-gray-700">
                      Create location labeling and identification system
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="receiving"
                    />
                    <label htmlFor="receiving" className="text-gray-700">
                      Establish receiving and shipping processes
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold mb-6">
            For Storage Space Hosts
          </h2>

          <div className="space-y-8">
            {/* Preparing Storage Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Preparing Your Storage Space for Tenants
              </h3>
              <p className="text-gray-700 mb-3">
                Essential preparation steps for hosts:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Ensure facility meets commercial building and fire codes
                </li>
                <li>
                  Clean entire space and remove all debris and abandoned items
                </li>
                <li>
                  Repair any structural damage to floors, walls, or roof
                </li>
                <li>Verify all loading docks and doors are operational</li>
                <li>
                  Test and service HVAC systems for proper climate control
                </li>
                <li>Install adequate lighting throughout warehouse areas</li>
                <li>
                  Mark clear fire lanes, exits, and emergency equipment locations
                </li>
                <li>
                  Provide detailed specifications: dimensions, ceiling heights,
                  load capacities
                </li>
                <li>Install basic security systems at minimum</li>
              </ul>
            </div>

            {/* Listing Information */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Essential Information for Your Listing
              </h3>
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Measurements & Specs</h4>
                  <p className="text-sm text-gray-700">
                    Total square footage, clear ceiling height, column spacing,
                    floor load capacity (PSF), truck court depth
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Loading & Access</h4>
                  <p className="text-sm text-gray-700">
                    Number and dimensions of loading docks, drive-in doors,
                    trailer parking spaces, access hours
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Climate & Environmental
                  </h4>
                  <p className="text-sm text-gray-700">
                    Temperature control capabilities, insulation, humidity
                    control, air quality systems
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Security & Safety Features
                  </h4>
                  <p className="text-sm text-gray-700">
                    Surveillance systems, access control, fire suppression,
                    security personnel, fencing
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Included Amenities</h4>
                  <p className="text-sm text-gray-700">
                    Existing racking, office space, restrooms, break rooms, WiFi,
                    parking
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing Strategies */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Pricing Your Storage Space
              </h3>
              <p className="text-gray-700 mb-3">
                Common pricing models for storage and warehouse spaces:
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Price Per Square Foot (Most Common)
                  </h4>
                  <p className="text-sm text-gray-600">
                    Monthly rate multiplied by total square footage. Typical
                    range: $4-15/sq ft/month depending on location and features.
                  </p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold mb-1">Pallet Position Pricing</h4>
                  <p className="text-sm text-gray-600">
                    Charge per pallet storage position. Good for shared warehouse
                    spaces. Typical: $15-50/pallet/month.
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Cubic Footage Pricing
                  </h4>
                  <p className="text-sm text-gray-600">
                    Charge based on actual volume used. Best for high-ceiling
                    warehouses with vertical storage.
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Value-Added Service Pricing
                  </h4>
                  <p className="text-sm text-gray-600">
                    Base storage rate plus fees for additional services:
                    receiving, picking, packing, shipping.
                  </p>
                </div>
              </div>
            </div>

            {/* Tenant Management */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Managing Storage Tenants
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">
                    Screening Potential Tenants
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>Verify business legitimacy and financial stability</li>
                    <li>
                      Understand what products will be stored (no prohibited
                      items)
                    </li>
                    <li>Confirm adequate insurance coverage</li>
                    <li>
                      Check references from previous warehouse landlords
                    </li>
                    <li>Ensure they have proper business licenses/permits</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Lease Terms to Include</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      Prohibited items (hazardous materials, perishables without
                      refrigeration)
                    </li>
                    <li>
                      Maintenance responsibilities (tenant vs. landlord
                      obligations)
                    </li>
                    <li>Access hours and notification requirements</li>
                    <li>Modification and improvement restrictions</li>
                    <li>
                      Subleasing and space-sharing policies
                    </li>
                    <li>Insurance requirements and liability limits</li>
                    <li>Security deposit (typically 2-3 months rent)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Risk Management for Hosts
              </h3>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Protect yourself with proper
                  insurance and legal safeguards for your storage facility.
                </p>
              </div>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  <strong>Property Insurance:</strong> Coverage for building
                  damage and loss
                </li>
                <li>
                  <strong>Liability Insurance:</strong> Protection against
                  injury or damage claims
                </li>
                <li>
                  <strong>Business Interruption:</strong> Coverage for lost rent
                  if facility becomes unusable
                </li>
                <li>
                  <strong>Regular Inspections:</strong> Monthly walkthroughs to
                  identify issues early
                </li>
                <li>
                  <strong>Tenant Insurance Requirements:</strong> Mandate tenants
                  carry their own coverage
                </li>
                <li>
                  <strong>Clear Liability Waivers:</strong> Define
                  responsibilities in lease agreements
                </li>
                <li>
                  <strong>Emergency Procedures:</strong> Documented plans for
                  fires, floods, and other disasters
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
